<template>
  <div class="sent-container">
    <!-- Folder Tabs -->
    <v-card class="mb-4" flat>
      <v-tabs
        v-model="activeTab"
        color="primary"
        align-tabs="center"
        class="folder-tabs"
      >
        <v-tab value="all">
          <v-icon start>mdi-send</v-icon>
          الكل ({{ messages.length }})
        </v-tab>
        
        <v-tab
          v-for="folder in userFolders"
          :key="folder.id"
          :value="folder.id.toString()"
        >
          <v-icon start :color="folder.color">mdi-folder</v-icon>
          {{ folder.name }} ({{ folder.emailCount || 0 }})
          <v-menu location="bottom" offset-y>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                size="x-small"
                variant="text"
                @click.stop
                class="mr-1"
              >
                <v-icon size="small">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="editFolder(folder)">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-pencil</v-icon>
                </template>
                <v-list-item-title>تعديل</v-list-item-title>
              </v-list-item>
              <v-list-item @click="confirmDeleteFolder(folder)">
                <template v-slot:prepend>
                  <v-icon size="small" color="error">mdi-delete</v-icon>
                </template>
                <v-list-item-title class="text-error">حذف</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-tab>
        
        <v-tab 
        v-if="hasPermission('admin.user-folders.store')"
        value="add" @click="openCreateFolderDialog">
          <v-icon start>mdi-folder-plus</v-icon>
          إضافة مجلد
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Toolbar -->
    <v-card class="mb-4 sent-toolbar" flat>
      <v-card-text class="pa-2">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="ml-2">
            <v-checkbox
              v-model="selectAll"
              hide-details
              density="compact"
              color="primary"
              @change="toggleSelectAll"
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto">
            <v-menu location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  size="small"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="selectAll = true; toggleSelectAll()">
                  <v-list-item-title>تحديد الكل</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectAll = false; toggleSelectAll()">
                  <v-list-item-title>إلغاء تحديد الكل</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              :disabled="!hasSelected"
              @click="refreshMessages"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
          
          
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              color="error"
              :disabled="!hasSelected"
              @click="deleteSelected"
            >
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </v-col>
          
          <v-spacer></v-spacer>
          
          <v-col cols="auto">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="بحث في المراسلات"
              single-line
              hide-details
              density="compact"
              variant="outlined"
              class="search-field"
            ></v-text-field>
          </v-col>
          
          <v-col cols="auto" class="mr-2">
            <v-btn
              icon
              variant="text"
              size="small"
              @click="toggleFilterMenu"
            >
              <v-icon>mdi-filter-outline</v-icon>
            </v-btn>
            <v-menu
              v-model="showFilterMenu"
              location="bottom"
              :close-on-content-click="false"
            >
              <v-card min-width="300">
                <v-card-title class="text-subtitle-1">تصفية المراسلات</v-card-title>
                <v-card-text>
                  <v-radio-group v-model="filter" hide-details>
                    <v-radio value="all" label="الكل"></v-radio>
                    <v-radio value="delivered" label="تم التسليم"></v-radio>
                    <v-radio value="read" label="تم القراءة"></v-radio>
                    <v-radio value="important" label="مهم"></v-radio>
                  </v-radio-group>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="text"
                    color="primary"
                    @click="showFilterMenu = false"
                  >
                    تطبيق
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <!-- Messages List -->
    <v-card flat class="sent-list">
      <div v-if="groupedMessages.length === 0" class="text-center pa-8">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-email-outline</v-icon>
        <div class="text-h6 text-grey">لا توجد مراسلات</div>
        <div class="text-body-2 text-grey">لم يتم إرسال أي مراسلات بعد</div>
      </div>
      
      <div v-for="group in groupedMessages" :key="group.date.toDateString()">
        <!-- Date Header -->
        <v-card 
          class="ma-2 date-header" 
          flat 
          color="white"
          :class="{ 'expanded': expandedDates[group.date.toDateString()] }"
          @click="toggleDateExpansion(group.date.toDateString())"
        >
          <v-card-text class="py-3 px-4 d-flex align-center justify-center">
            <v-icon 
              :class="{ 'rotated': expandedDates[group.date.toDateString()] }" 
              class="mr-2 transition-transform"
            >
              mdi-chevron-right
            </v-icon>
            <span class="text-subtitle-1 font-weight-medium">
              {{ formatDateHeader(group.date) }}
            </span>
        
          </v-card-text>
        </v-card>
        
        <!-- Messages for this date -->
        <v-expand-transition>
          <div v-show="expandedDates[group.date.toDateString()]">
            <v-list lines="two" class="pa-0">
              <template v-for="message in group.messages" :key="message.id">
                <v-list-item
                  :class="{ 'selected-message': selectedMessages.includes(message.id) }"
                  @click="openMessage(message)"
                  class="mx-2 mb-1 rounded"
                >
                  <template v-slot:prepend>
                    <div class="d-flex align-center">
                      <v-checkbox
                        v-model="selectedMessages"
                        :value="message.id"
                        hide-details
                        density="compact"
                        color="primary"
                        @click.stop
                      ></v-checkbox>
                      <!-- <v-btn
                        icon
                        variant="text"
                        size="small"
                        color="amber-darken-2"
                        @click.stop="toggleImportant(message)"
                      >
                        <v-icon>{{ message.important ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                      </v-btn> -->
                    </div>
                  </template>
                  
                  <v-list-item-title>
                    <div class="recipients-display">
                      <span v-if="message.to && message.to.length > 0" class="recipient-group">
                        <strong>إلى:</strong> {{ message.to.join('، ') }}
                      </span>
                   
                    </div>
                  </v-list-item-title>
                  
                  <v-list-item-subtitle class="d-flex">
                    <span class="text-truncate">
                      {{ message.subject }} - {{ message.preview }}
                    </span>
                  </v-list-item-subtitle>
                  
                  <template v-slot:append>
                    <div class="d-flex flex-column align-end">
                      <span class="text-caption">
                        {{ formatTime(message.date) }}
                      </span>
                      <div class="mt-1 d-flex align-center">
                        <v-chip
                          v-if="message.attachment"
                          size="x-small"
                          color="grey-lighten-3"
                          class="mr-1"
                        >
                          <v-icon size="x-small" start>mdi-paperclip</v-icon>
                        </v-chip>
                        <v-chip
                          v-if="message.urgent"
                          size="x-small"
                          color="error"
                          text-color="white"
                          class="mr-1"
                        >
                          عاجل
                        </v-chip>
                        <v-tooltip location="top">
                          <template v-slot:activator="{ props }">
                            <v-icon
                              v-bind="props"
                              size="small"
                              :color="getStatusColor(message)"
                            >
                              {{ getStatusIcon(message) }}
                            </v-icon>
                          </template>
                          {{ getStatusText(message) }}
                        </v-tooltip>
                        
                        <v-menu location="start">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon
                              variant="text"
                              size="small"
                              @click.stop
                            >
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list density="compact">
                            <v-list-item @click="openTrackMailDialog(message)">
                              <template v-slot:prepend>
                                <v-icon color="primary">mdi-radar</v-icon>
                              </template>
                              <v-list-item-title>تتبع الرسالة</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="toggleImportant(message)">
                              <template v-slot:prepend>
                                <v-icon>{{ message.important ? 'mdi-star-off' : 'mdi-star' }}</v-icon>
                              </template>
                              <v-list-item-title>
                                {{ message.important ? 'إزالة أهمية' : 'وضع علامة مهم' }}
                              </v-list-item-title>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item color="error">
                              <template v-slot:prepend>
                                <v-icon color="error">mdi-delete</v-icon>
                              </template>
                              <v-list-item-title>حذف</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </template>
                </v-list-item>
              </template>
            </v-list>
          </div>
        </v-expand-transition>
      </div>
      
      <!-- Loading More Indicator -->
      <div v-if="loadingMore" class="text-center pa-4">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        <div class="text-body-2 text-grey mt-2">جاري تحميل المزيد من الرسائل...</div>
      </div>
      
      <!-- No More Messages Indicator -->
      <div v-else-if="!hasMoreMails && messages.length > 0" class="text-center pa-4">
        <v-icon size="32" color="grey-lighten-1" class="mb-2">mdi-check-circle</v-icon>
        <div class="text-body-2 text-grey">تم تحميل جميع الرسائل</div>
      </div>
    </v-card>
  </div>
  
  <!-- Message Detail Dialog -->
  <v-dialog v-model="messageDialog" max-width="800">
    <v-card v-if="currentMessage">
      <v-card-title class="d-flex justify-space-between align-center">
        <div>{{ currentMessage.subject }}</div>
        <v-btn icon variant="text" @click="messageDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-subtitle>
        <div class="d-flex justify-space-between align-start">
          <div class="recipients-detail">
            <div v-if="currentMessage.to && currentMessage.to.length > 0" class="mb-1">
              <strong>إلى: </strong>
              <span>{{ currentMessage.to.join('، ') }}</span>
            </div>
            <div v-if="currentMessage.cc && currentMessage.cc.length > 0" class="mb-1">
              <strong>نسخة: </strong>
              <span>{{ currentMessage.cc.join('، ') }}</span>
            </div>
            <div v-if="currentMessage.fyi && currentMessage.fyi.length > 0" class="mb-1">
              <strong>للعلم: </strong>
              <span>{{ currentMessage.fyi.join('، ') }}</span>
            </div>
            <div v-if="currentMessage.ref" class="mb-1 text-primary">
              <strong>الرقم المرجعي: </strong>
              <span>{{ currentMessage.ref }}</span>
            </div>
          </div>
          <div class="text-grey">{{ formatDate(currentMessage.date) }}</div>
        </div>
      </v-card-subtitle>
      
      <v-divider></v-divider>
      
      <v-card-text class="message-content">
        <div v-html="currentMessage.content"></div>
        
        <div v-if="currentMessage.attachment" class="mt-6">
          <v-divider class="mb-4"></v-divider>
          <div class="text-subtitle-2 mb-2">المرفقات ({{ currentMessage.attachments?.length || 1 }})</div>
          
          <div v-for="(attachment, i) in currentMessage.attachments || [{name: 'document.pdf', size: '2.3 MB'}]" :key="i" class="d-flex align-center pa-2 attachment-item">
            <v-icon color="grey" class="mr-2">
              {{ getAttachmentIcon(attachment.name) }}
            </v-icon>
            <div>
              <div>{{ attachment.name }}</div>
              <div class="text-caption text-grey">{{ attachment.size }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" size="small">
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions>
        <v-btn v-if="hasPermission('admin.mails.store-forward')" prepend-icon="mdi-share" variant="text">
          إعادة توجيه
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="hasPermission('admin.mails.history')" prepend-icon="mdi-radar" variant="text" color="primary" @click="openTrackMailDialog(currentMessage)">
          تتبع الرسالة
        </v-btn>
        <v-btn prepend-icon="mdi-printer" variant="text" color="success" @click="printMessage">
          طباعة
        </v-btn>
        <v-btn v-if="hasPermission('admin.mails.destroy')" prepend-icon="mdi-delete" variant="text" color="error">
          حذف
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

    <!-- Create/Edit Folder Dialog -->
    <v-dialog v-model="createFolderDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">
          {{ editingFolder ? 'تعديل المجلد' : 'إنشاء مجلد جديد' }}
        </v-card-title>
        
        <v-form ref="folderFormRef" v-model="folderFormValid" @submit.prevent="saveFolder">
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="folderForm.name"
                  label="اسم المجلد"
                  required
                  :rules="[v => !!v || 'اسم المجلد مطلوب']"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="folderForm.desc"
                  label="وصف المجلد"
                  variant="outlined"
                  density="comfortable"
                  rows="3"
                ></v-textarea>
              </v-col>
              
              <v-col cols="12">
                <v-select
                  v-model="folderForm.color"
                  label="لون المجلد"
                  :items="colorOptions"
                  item-title="name"
                  item-value="value"
                  variant="outlined"
                  density="comfortable"
                >
                  <template v-slot:selection="{ item }">
                    <v-icon :color="item.raw.value" class="mr-2">{{ item.raw.icon }}</v-icon>
                    {{ item.raw.name }}
                  </template>
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-icon :color="item.raw.value">{{ item.raw.icon }}</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="createFolderDialog = false">إلغاء</v-btn>
            <v-btn 
              color="primary" 
              type="submit"
              :disabled="!folderFormValid"
              :loading="loading"
            >
              {{ editingFolder ? 'تحديث' : 'إنشاء' }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Delete Folder Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-delete-alert</v-icon>
          تأكيد الحذف
        </v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف المجلد "<strong>{{ folderToDelete?.name }}</strong>"؟
          <br><br>
          <v-alert type="warning" density="compact" class="mt-2">
            سيتم حذف المجلد نهائياً ولا يمكن التراجع عن هذا الإجراء.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn 
            color="error" 
            @click="executeDeleteFolder"
            :loading="loading"
          >
            حذف نهائياً
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Track Mail Dialog -->
    <v-dialog v-model="trackMailDialog" max-width="1000px" scrollable>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-radar</v-icon>
          تتبع الرسالة
          <v-spacer></v-spacer>
          <v-btn icon @click="trackMailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-subtitle v-if="currentMailForTracking" class="pb-0">
          <div><strong>الموضوع:</strong> {{ currentMailForTracking.subject }}</div>
          <div><strong>الرقم المرجعي:</strong> {{ mailHistory?.ref || 'غير متوفر' }}</div>
        </v-card-subtitle>
        
        <v-card-text>
          <v-data-table
            :headers="trackingHeaders"
            :items="mailHistoryItems"
            :loading="trackingLoading"
            class="elevation-1"
            density="compact"
            :items-per-page="10"
          >
            <template #[`item.date`]="{ item }">
              <div class="text-body-2">
                {{ formatFullDate(item.date) }}
              </div>
            </template>
            
            <template #[`item.operation_type`]="{ item }">
              <v-chip
                :color="getOperationTypeColor(item.operation_type)"
                size="small"
                variant="outlined"
              >
                <v-icon start size="small">{{ getOperationTypeIcon(item.operation_type) }}</v-icon>
                {{ getOperationTypeText(item.operation_type) }}
              </v-chip>
            </template>
            
            <template #[`item.from_department`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="me-2" size="small" color="primary">mdi-office-building</v-icon>
                {{ item.from_department }}
              </div>
            </template>
            
            <template #[`item.to_departments`]="{ item }">
              <div class="d-flex flex-wrap ga-1">
                <v-chip
                  v-for="dept in item.to_departments"
                  :key="dept"
                  size="x-small"
                  color="info"
                  variant="outlined"
                >
                  {{ dept }}
                </v-chip>
              </div>
            </template>
            
            <template #[`item.sender`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="me-2" size="small" color="success">mdi-account</v-icon>
                {{ item.sender }}
              </div>
            </template>
          </v-data-table>
          
          <v-alert
            v-if="mailHistoryItems.length === 0 && !trackingLoading"
            type="info"
            variant="tonal"
            class="mt-4"
          >
            <v-icon>mdi-information</v-icon>
            لا يوجد تاريخ متاح لهذه الرسالة
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="trackMailDialog = false"
          >
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  
    <!-- Hidden Print Template -->
    <div ref="printContent" style="display: none;">
      <div class="print-page-with-bg" v-if="currentMessage">
        <!-- Addressee Section -->
        <div class="print-addressee">
          <p>السيد / {{ getRecipientTitle() }}</p>
        </div>

        <!-- Mail Content -->
        <div class="print-mail-content">
          <div class="mail-body" v-html="currentMessage.content?.replace(/\n/g, '<br>')"></div>
        </div>

        <!-- Sender Section -->
        <div class="print-sender-section">
          <div class="sender-copy-note">
            <i class="mdi mdi-archive"></i>
            <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف
          </div>
          <div class="sender-left-group">
            <div class="sender-info-text">
              <div class="sender-name">{{ getSenderName() }}</div>
              <div class="sender-position">
                {{ getSenderJobTitle() }}
              </div>
            </div>
            
            <div class="stamps-section">
              <!-- Department Stamp -->
              <div class="department-stamp">
                <img 
                  v-if="currentMessage.senderDepartmentStamp"
                  :src="currentMessage.senderDepartmentStamp" 
                  alt="ختم القسم"
                  class="stamp-image department-stamp-img"
                />
                <div v-else class="stamp-placeholder">
                  <div class="stamp-text">ختم القسم</div>
                </div>
              </div>
              
              <!-- Sender Signature -->
              <div class="sender-signature">
                <img 
                  v-if="currentMessage.senderStamp"
                  :src="currentMessage.senderStamp" 
                  alt="توقيع المرسل"
                  class="signature-image"
                />
                <div v-else class="signature-placeholder">
                  <div class="signature-line">التوقيع</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  
</template>

<script>
import userFolderService from '../services/userFolderService'
import mailService from '../services/mailService'
import { hasPermission, hasModule } from '@/utils/permissions'


export default {
  name: 'SentView',
  data() {
    return {
      search: '',
      filter: 'all',
      selectAll: false,
      selectedMessages: [],
      expandedDates: {}, // Track which date sections are expanded
      showFilterMenu: false,
      messageDialog: false,
      currentMessage: null,
      activeTab: 'all',
      userFolders: [],
      createFolderDialog: false,
      deleteDialog: false,
      folderToDelete: null,
      editingFolder: null,
      folderFormValid: false,
      loading: false,
      folderForm: {
        name: '',
        desc: '',
        color: '#2196F3'
      },
      colorOptions: [
        { name: 'أزرق', value: '#2196F3', icon: 'mdi-circle' },
        { name: 'أحمر', value: '#F44336', icon: 'mdi-circle' },
        { name: 'أخضر', value: '#4CAF50', icon: 'mdi-circle' },
        { name: 'برتقالي', value: '#FF9800', icon: 'mdi-circle' },
        { name: 'بنفسجي', value: '#9C27B0', icon: 'mdi-circle' },
        { name: 'وردي', value: '#E91E63', icon: 'mdi-circle' },
        { name: 'أزرق فاتح', value: '#00BCD4', icon: 'mdi-circle' },
        { name: 'رمادي', value: '#607D8B', icon: 'mdi-circle' }
      ],
      messages: [], // Will be filled by API
      // Pagination properties
      lastMailId: null,
      hasMoreMails: true,
      loadingMore: false,
      trackMailDialog: false,
      currentMailForTracking: null,
      mailHistory: null,
      trackingLoading: false,
      trackingHeaders: [
        { title: 'التاريخ', key: 'date', align: 'start', sortable: true },
        { title: 'العملية', key: 'operation_type', align: 'start', sortable: true },
        { title: 'من القسم', key: 'from_department', align: 'start', sortable: true },
        { title: 'إلى الأقسام', key: 'to_departments', align: 'start', sortable: true },
        { title: 'المرسل', key: 'sender', align: 'start', sortable: true }
      ],
      mailHistoryItems: []
    }
  },
  computed: {
    filteredMessages() {
      let result = [...this.messages];
      
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(message => 
          (message.to && message.to.some(recipient => recipient.toLowerCase().includes(searchLower))) ||
          (message.cc && message.cc.some(recipient => recipient.toLowerCase().includes(searchLower))) ||
          (message.fyi && message.fyi.some(recipient => recipient.toLowerCase().includes(searchLower))) ||
          (message.subject && message.subject.toLowerCase().includes(searchLower)) ||
          (message.preview && message.preview.toLowerCase().includes(searchLower)) ||
          (message.priority && message.priority.toLowerCase().includes(searchLower)) ||
          (message.ref && message.ref.toLowerCase().includes(searchLower))
        );
      }
      
      if (this.filter === 'delivered') {
        result = result.filter(message => message.status === 'delivered');
      } else if (this.filter === 'read') {
        result = result.filter(message => message.status === 'read');
      } else if (this.filter === 'important') {
        result = result.filter(message => message.important);
      }
      
      return result.sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    
    groupedMessages() {
      const groups = {};
      
      this.filteredMessages.forEach(message => {
        const messageDate = new Date(message.date);
        const dateKey = messageDate.toDateString();
        
        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: messageDate,
            messages: []
          };
          // Set as expanded by default
          this.expandedDates[dateKey] = true;
        }
        
        groups[dateKey].messages.push(message);
      });
      
      // Convert to array and sort by date (newest first)
      return Object.values(groups).sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    hasSelected() {
      return this.selectedMessages.length > 0;
    },
    otherFolders() {
      return this.userFolders.filter(folder => folder.id.toString() !== this.activeTab);
    }
  },
  async mounted() {
    await this.loadUserFolders()
    await this.loadSentMessages()
    
    // Add scroll listener for infinite scrolling
    this.setupInfiniteScroll()
  },

  beforeUnmount() {
    // Remove scroll listener
    this.removeInfiniteScroll()
  },

  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadUserFolders() {
      try {
        this.loading = true
        const response = await userFolderService.getUserFolders({ sent: true })
        // Filter folders where sent is true and try to support both response formats
        const foldersData = (response.data && response.data.data) || response.data || []
        this.userFolders = foldersData.filter(folder => folder.sent === true)
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في تحميل المجلدات: ' + error.message
        })
      } finally {
        this.loading = false
      }
    },

    async loadSentMessages(isLoadMore = false) {
      try {
        if (isLoadMore) {
          this.loadingMore = true
        } else {
          this.loading = true
          this.messages = [] // Reset messages for initial load
          this.lastMailId = null
          this.hasMoreMails = true
        }
        
        const response = await mailService.getSentMails(this.lastMailId)
        const newMails = response.data?.data || response.data || []
        
        // Map API response to the messages array structure used in the UI
        const mappedMails = newMails.map(mail => {
          // Filter departments by recipient type (case-insensitive)
          const toDepartments = mail.departments?.filter(dept => 
            dept.pivot?.recipient_type?.toUpperCase() === 'TO'
          ) || []
          
          const ccDepartments = mail.departments?.filter(dept => 
            dept.pivot?.recipient_type?.toUpperCase() === 'CC'
          ) || []
          
          const fyiDepartments = mail.departments?.filter(dept => 
            dept.pivot?.recipient_type?.toUpperCase() === 'FYI'
          ) || []

          return {
            id: mail.id,
            to: this.mapDepartmentNames(toDepartments),
            cc: this.mapDepartmentNames(ccDepartments),
            fyi: this.mapDepartmentNames(fyiDepartments),
            subject: mail.subject || 'بدون موضوع',
            preview: mail.body ? this.stripHtml(mail.body).slice(0, 80) + (this.stripHtml(mail.body).length > 80 ? '...' : '') : '',
            content: mail.body || '',
            date: mail.sent_at || mail.created_at,
            important: false, // You can add logic to determine importance
            attachment: (mail.attachments && mail.attachments.length > 0),
            attachments: mail.attachments || [],
            mailType: mail.mail_type?.name || '',
            mail_type: mail.mail_type, // Keep the full object for printing
            sender: mail.sender?.name || '',
            senderJob: mail.sender_department_job?.name || '',
            senderJobShort: mail.sender?.employee_title?.short_name || '',
            senderDepartment: mail.sender_department?.name || '',
            senderStamp: mail.sender?.stamp_url || '',
            senderDepartmentStamp: mail.sender_department?.stamp_url || '',
            urgent: mail.priority?.level >= 4, // Example logic for urgent based on priority level
            status: this.determineMailStatus(mail), // Custom method to determine status
            priority: mail.priority?.name || '',
            ref: mail.ref || 'غير متوفر' // Add fallback for missing ref
          }
        })
        
        if (isLoadMore) {
          // Append new mails to existing ones
          this.messages = [...this.messages, ...mappedMails]
        } else {
          // Replace with new mails for initial load
          this.messages = mappedMails
        }
        
        // Update pagination state
        if (mappedMails.length > 0) {
          // Since we're sorting by sent_at but filtering by id, use the lowest id for next cursor
          this.lastMailId = mappedMails[mappedMails.length - 1].id
          this.hasMoreMails = mappedMails.length === 10 // If we got less than 10, no more mails
          //console.log('Updated sent pagination:', { lastMailId: this.lastMailId, hasMoreMails: this.hasMoreMails });
        } else {
          this.hasMoreMails = false
          //console.log('No more sent mails available');
        }
        
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في تحميل المراسلات المرسلة: ' + (error.message || 'خطأ غير معروف')
        })
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    mapDepartmentNames(departments) {
      return departments.map(dept => dept.name).filter(Boolean)
    },

    determineMailStatus(mail) {
      // Example logic to determine mail status - adjust based on your API data
      if (mail.read_at) {
        return 'read'
      } else if (mail.delivered_at) {
        return 'delivered'
      } else {
        return 'pending'
      }
    },

    openCreateFolderDialog() {
      this.editingFolder = null
      this.folderForm = {
        name: '',
        desc: '',
        color: '#2196F3'
      }
      this.folderFormValid = false
      this.createFolderDialog = true
    },

    editFolder(folder) {
      this.editingFolder = folder
      this.folderForm = {
        name: folder.name,
        desc: folder.desc || '',
        color: folder.color || '#2196F3'
      }
      this.folderFormValid = true
      this.createFolderDialog = true
    },

    async saveFolder() {
      try {
        this.loading = true
        
        if (this.editingFolder) {
          await userFolderService.updateUserFolder(this.editingFolder.id, this.folderForm)
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'success',
            color: 'success',
            icon: 'mdi-check-circle',
            timeout: 3000,
            closable: true,
            message: 'تم تحديث المجلد بنجاح'
          })
        } else {
          await userFolderService.createUserFolder({ ...this.folderForm, sent: true })
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'success',
            color: 'success',
            icon: 'mdi-check-circle',
            timeout: 3000,
            closable: true,
            message: 'تم إنشاء المجلد بنجاح'
          })
        }
        
        this.createFolderDialog = false
        await this.loadUserFolders()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: error.message
        })
      } finally {
        this.loading = false
      }
    },

    confirmDeleteFolder(folder) {
      this.folderToDelete = folder
      this.deleteDialog = true
    },

    async executeDeleteFolder() {
      try {
        this.loading = true
        await userFolderService.deleteUserFolder(this.folderToDelete.id)
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: 'تم حذف المجلد بنجاح'
        })
        
        this.deleteDialog = false
        this.folderToDelete = null
        
        // If the deleted folder was active, switch to "all"
        if (this.activeTab === this.folderToDelete?.id.toString()) {
          this.activeTab = 'all'
        }
        
        await this.loadUserFolders()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: error.message
        })
      } finally {
        this.loading = false
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedMessages = this.filteredMessages.map(m => m.id);
      } else {
        this.selectedMessages = [];
      }
    },
    toggleFilterMenu() {
      this.showFilterMenu = !this.showFilterMenu;
    },
    formatDate(date) {
      const now = new Date();
      const messageDate = new Date(date);
      
      if (messageDate.toDateString() === now.toDateString()) {
        return messageDate.toLocaleTimeString('ar-LY', { hour: '2-digit', minute: '2-digit' });
      }
      
      if (messageDate.getFullYear() === now.getFullYear()) {
        return messageDate.toLocaleDateString('ar-LY', { day: 'numeric', month: 'short' });
      }
      
      return messageDate.toLocaleDateString('ar-LY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getStatusIcon(message) {
      switch (message.status) {
        case 'read':
          return 'mdi-email-check';
        case 'delivered':
          return 'mdi-email-check-outline';
        default:
          return 'mdi-email-clock-outline';
      }
    },
    getStatusColor(message) {
      switch (message.status) {
        case 'read':
          return 'success';
        case 'delivered':
          return 'primary';
        default:
          return 'grey';
      }
    },
    getStatusText(message) {
      switch (message.status) {
        case 'read':
          return 'تم القراءة';
        case 'delivered':
          return 'تم التسليم';
        default:
          return 'قيد الإرسال';
      }
    },
    toggleImportant(message) {
      message.important = !message.important;
    },
    deleteSelected() {
      this.messages = this.messages.filter(message => !this.selectedMessages.includes(message.id));
      this.selectedMessages = [];
      this.selectAll = false;
    },
    refreshMessages() {
      this.selectedMessages = [];
      this.selectAll = false;
      this.loadUserFolders();
      this.loadSentMessages();
    },
    openMessage(message) {
      this.currentMessage = message;
      this.messageDialog = true;
    },
    toggleDateExpansion(dateKey) {
      this.expandedDates[dateKey] = !this.expandedDates[dateKey];
    },
    
    formatDateHeader(date) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'اليوم';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'أمس';
      } else {
        return date.toLocaleDateString('ar-LY', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
    },
    
    formatTime(date) {
      return new Date(date).toLocaleTimeString('ar-LY', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },

    getAttachmentIcon(filename) {
      // Handle null, undefined, or empty filename
      if (!filename || typeof filename !== 'string') {
        return 'mdi-file-document-outline';
      }
      
      const extension = filename.split('.').pop().toLowerCase();
      
      switch (extension) {
        case 'pdf':
          return 'mdi-file-pdf-box';
        case 'doc':
        case 'docx':
          return 'mdi-file-word';
        case 'xls':
        case 'xlsx':
          return 'mdi-file-excel';
        case 'ppt':
        case 'pptx':
          return 'mdi-file-powerpoint';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'mdi-file-image';
        default:
          return 'mdi-file-document-outline';
      }
    },

    // Infinite scroll methods
    setupInfiniteScroll() {
      // Since the whole page scrolls, use window scroll events
      //console.log('Setting up infinite scroll for sent view on window');
      window.addEventListener('scroll', this.handleScroll);
    },

    removeInfiniteScroll() {
      //console.log('Removing infinite scroll listener for sent view');
      window.removeEventListener('scroll', this.handleScroll);
    },

    handleScroll() {
      if (this.loadingMore || !this.hasMoreMails) {
        //console.log('Sent scroll ignored - loadingMore:', this.loadingMore, 'hasMoreMails:', this.hasMoreMails);
        return;
      }
      
      const threshold = 300; // Load more when 300px from bottom
      
      // Use window/document scroll properties
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
      
      //console.log('Sent window scroll data:', { 
        //scrollTop, 
        //windowHeight, 
        //documentHeight, 
        //distanceFromBottom, 
        //threshold,
        //shouldLoadMore: distanceFromBottom <= threshold
      //});
      
      if (distanceFromBottom <= threshold) {
        //console.log('Loading more sent mails triggered by window scroll');
        this.loadMoreMails();
      }
    },

    async loadMoreMails() {
      if (this.loadingMore || !this.hasMoreMails) {
        //console.log('loadMoreSentMails ignored - loadingMore:', this.loadingMore, 'hasMoreMails:', this.hasMoreMails);
        return;
      }
      
      //console.log('Loading more sent mails, lastMailId:', this.lastMailId);
      await this.loadSentMessages(true);
    },

    async loadMailHistory(mailId) {
      this.trackingLoading = true;
      this.mailHistoryItems = []; // Clear previous history
      try {
        const response = await mailService.getMailHistory(mailId);
        //console.log('Mail history response:', response);
        
        this.mailHistory = response.data || response;
        
        // Parse the history similar to InboxView
        const items = [];
        
        // Add the original mail as the first item (SEND operation)
        items.push({
          date: this.mailHistory.sent_at || this.mailHistory.created_at,
          operation_type: 'send',
          from_department: this.mailHistory.sender_department?.name || 'غير محدد',
          to_departments: this.mailHistory.departments?.map(dept => dept.name) || [],
          sender: this.mailHistory.sender?.name || 'غير محدد'
        });
        
        // Add history items
        if (this.mailHistory.history && Array.isArray(this.mailHistory.history)) {
          this.mailHistory.history.forEach(historyItem => {
            let operationType = 'send'; // default
            
            // Determine operation type based on parent_id and forward_parent_id
            if (historyItem.parent_id && !historyItem.forward_parent_id) {
              operationType = 'reply';
            } else if (historyItem.forward_parent_id && !historyItem.parent_id) {
              operationType = 'forward';
            }
            
            items.push({
              date: historyItem.sent_at || historyItem.created_at,
              operation_type: operationType,
              from_department: historyItem.sender_department?.name || 'غير محدد',
              to_departments: historyItem.departments?.map(dept => dept.name) || [],
              sender: historyItem.sender?.name || 'غير محدد'
            });
          });
        }
        
        // Sort by date (newest first)
        this.mailHistoryItems = items.sort((a, b) => new Date(b.date) - new Date(a.date));
        
      } catch (error) {
        console.error('Error loading mail history:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في تحميل تاريخ الرسالة: ' + (error.message || 'خطأ غير معروف')
        });
      } finally {
        this.trackingLoading = false;
      }
    },

    formatFullDate(date) {
      const fullDate = new Date(date);
      return fullDate.toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      });
    },

    getOperationTypeIcon(type) {
      switch (type) {
        case 'sent':
          return 'mdi-send';
        case 'delivered':
          return 'mdi-email-check';
        case 'read':
          return 'mdi-email-check-outline';
        case 'opened':
          return 'mdi-eye';
        case 'replied':
          return 'mdi-reply';
        case 'forwarded':
          return 'mdi-share';
        case 'deleted':
          return 'mdi-delete';
        default:
          return 'mdi-information';
      }
    },

    getOperationTypeColor(type) {
      switch (type) {
        case 'sent':
          return 'primary';
        case 'delivered':
          return 'success';
        case 'read':
          return 'info';
        case 'opened':
          return 'warning';
        case 'replied':
          return 'info';
        case 'forwarded':
          return 'info';
        case 'deleted':
          return 'error';
        default:
          return 'grey';
      }
    },

    getOperationTypeText(type) {
      switch (type) {
        case 'sent':
          return 'تم الإرسال';
        case 'delivered':
          return 'تم التسليم';
        case 'read':
          return 'تم القراءة';
        case 'opened':
          return 'تم فتح الرسالة';
        case 'replied':
          return 'تم الرد';
        case 'forwarded':
          return 'تم التوجيه';
        case 'deleted':
          return 'تم حذف الرسالة';
        default:
          return 'تم إرسال الرسالة';
      }
    },

    openTrackMailDialog(message) {
      this.currentMailForTracking = message;
      this.loadMailHistory(message.id);
      this.trackMailDialog = true;
    },

    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },

    // Print functionality - Same format as DashboardView
    async printMessage() {
      if (!this.currentMessage) return;
      
      try {
        // Get the background image as base64
        const backgroundImage = await this.getImageAsBase64();
        
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'error',
            color: 'error',
            icon: 'mdi-alert-circle',
            timeout: 3000,
            closable: true,
            message: 'تعذر فتح نافذة الطباعة'
          });
          return;
        }
        
        // Calculate content length to determine if we need multiple pages
        const contentText = this.stripHtml(this.currentMessage.preview || this.currentMessage.content || '');
        const contentLength = contentText.length;
        
        // More accurate estimation based on actual content area
        // First page has less space (45% height ≈ 1800 chars), continuation pages have more (65% ≈ 2600 chars)
        const firstPageChars = 1800;
        const continuationPageChars = 2600;
        
        let estimatedPages = 1;
        let remainingContent = contentLength;
        
        if (remainingContent > firstPageChars) {
          remainingContent -= firstPageChars;
          estimatedPages += Math.ceil(remainingContent / continuationPageChars);
        }
        
        //console.log('Print Debug:', {
          //contentLength,
          //firstPageChars,
          //continuationPageChars,
          //estimatedPages,
          //willUseMultiPage: estimatedPages > 1
        //});
        
        // Create multiple page divs
        let pagesHtml = '';
        
        if (estimatedPages <= 1) {
          // Single page layout
          pagesHtml = `
            <div class="print-page-with-bg">
              <!-- Date and Ref Section -->
              <div class="print-date-ref">
                <div class="ref-number">الإشاري: ${this.currentMessage.ref || 'غير متوفر'}</div>
                <div class="mail-date">التاريخ: ${this.formatPrintDate(this.currentMessage.date)}</div>
              </div>
              <div class="print-type-barcode">
                <div class="mail-type-label">النوع: ${this.currentMessage.mail_type?.name || 'غير متوفر'}</div>
                <svg class="barcode-svg" data-ref="${this.currentMessage.ref || ''}"></svg>
              </div>

              <!-- Mail Content Section -->
              <div class="print-mail-content first-page">
                <div class="mail-recipients">السادة : ${(this.currentMessage.to || []).join(' , ') || 'غير محدد'}</div>
                <div class="mail-subject">بخصوص : ${this.currentMessage.subject || 'بدون موضوع'}</div>
                <div class="mail-body">${this.currentMessage.content?.replace(/\n/g, '<br>') || ''}</div>
              </div>

              <!-- Sender Section -->
              <div class="print-sender-section">
                <div class="sender-copy-note">
                  <div><i class="mdi mdi-archive"></i> <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف</div>
                  ${(this.currentMessage.cc || []).map(name => `<div class="copy-recipient">${name}</div>`).join('')}
                </div>
                <div class="sender-left-group">
                  <div class="sender-info-text">
                    <div class="sender-name">${this.currentMessage.senderJobShort || '' } ${this.currentMessage.sender || ''}</div>
                    <div class="sender-position">${this.currentMessage.senderJob || '' } ${this.currentMessage.senderDepartment || ''}</div>
                  </div>
                  
                  <div class="stamps-section">
                    <div class="sender-signature">
                      ${this.currentMessage.senderStamp ? 
                        `<div class="signature-image" style="background-image: url('${this.currentMessage.senderStamp}');"></div>` :
                        '<div class="signature-placeholder"><div class="signature-line">التوقيع</div></div>'
                      }
                    </div>
                    <div class="department-stamp">
                      ${this.currentMessage.senderDepartmentStamp ? 
                        `<div class="department-stamp-img" style="background-image: url('${this.currentMessage.senderDepartmentStamp}');"></div>` :
                        '<div class="stamp-placeholder"><div class="stamp-text">ختم القسم</div></div>'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          `;
        } else {
          // Multi-page layout - split content accurately
          let currentIndex = 0;
          
          for (let page = 0; page < estimatedPages; page++) {
            const isFirstPage = page === 0;
            const isLastPage = page === estimatedPages - 1;
            
            let pageChars = isFirstPage ? firstPageChars : continuationPageChars;
            let endIndex = Math.min(currentIndex + pageChars, contentLength);
            let pageContent = contentText.slice(currentIndex, endIndex);
            
            // Try to break at word boundaries when possible (but not on last page)
            if (!isLastPage && endIndex < contentLength) {
              const lastSpaceIndex = pageContent.lastIndexOf(' ');
              if (lastSpaceIndex > pageContent.length * 0.7) { // Only adjust if space is reasonably near the end
                pageContent = pageContent.slice(0, lastSpaceIndex);
                endIndex = currentIndex + lastSpaceIndex;
              }
            }
            
            // Convert back to HTML format
            pageContent = pageContent.replace(/\n/g, '<br>');
            
            pagesHtml += `
              <div class="print-page-with-bg">
                ${isFirstPage ? `
                  <!-- Date and Ref Section -->
                  <div class="print-date-ref">
                    <div class="ref-number">الإشاري: ${this.currentMessage.ref || 'غير متوفر'}</div>
                    <div class="mail-date">التاريخ: ${this.formatPrintDate(this.currentMessage.date)}</div>
                  </div>
                  <div class="print-type-barcode">
                    <div class="mail-type-label">النوع: ${this.currentMessage.mail_type?.name || 'غير متوفر'}</div>
                    <svg class="barcode-svg" data-ref="${this.currentMessage.ref || ''}"></svg>
                  </div>
                ` : ''}

                <!-- Mail Content Section -->
                <div class="print-mail-content ${isFirstPage ? 'first-page' : 'continuation-page'}">
                  ${isFirstPage ? `
                    <div class="mail-recipients">السادة : ${(this.currentMessage.to || []).join(' , ') || 'غير محدد'}</div>
                    <div class="mail-subject">الموضوع : ${this.currentMessage.subject || 'بدون موضوع'}</div>
                  ` : ''}
                  <div class="mail-body">${pageContent}</div>
                </div>

                ${isLastPage ? `
                  <!-- Sender Section -->
                  <div class="print-sender-section">
                    <div class="sender-copy-note">
                      <div><i class="mdi mdi-archive"></i> <span style="font-weight: bold;">صورة إلى :</span> الأرشــيــف</div>
                      ${(this.currentMessage.cc || []).map(name => `<div class="copy-recipient">${name}</div>`).join('')}
                    </div>
                    <div class="sender-left-group">
                      <div class="sender-info-text">
                        <div class="sender-name">${this.currentMessage.senderJobShort || '' } ${this.currentMessage.sender || ''}</div>
                        <div class="sender-position">${this.currentMessage.senderJob || '' } ${this.currentMessage.senderDepartment || ''}</div>
                      </div>
                      
                      <div class="stamps-section">
                        <div class="sender-signature">
                          ${this.currentMessage.senderStamp ? 
                            `<div class="signature-image" style="background-image: url('${this.currentMessage.senderStamp}');"></div>` :
                            '<div class="signature-placeholder"><div class="signature-line">التوقيع</div></div>'
                          }
                        </div>
                        <div class="department-stamp">
                          ${this.currentMessage.senderDepartmentStamp ? 
                            `<div class="department-stamp-img" style="background-image: url('${this.currentMessage.senderDepartmentStamp}');"></div>` :
                            '<div class="stamp-placeholder"><div class="stamp-text">ختم القسم</div></div>'
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                ` : ''}
              </div>
            `;
            
            currentIndex = endIndex;
          }
        }
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>طباعة المراسلة</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@200..1000&display=swap" rel="stylesheet">
            <style>
              * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
                font-family: 'cairo', sans-serif; 
              }
              body { 
                font-family: 'cairo', sans-serif; 
                direction: rtl; 
                margin: 0;
                padding: 0;
              }
              @page { 
                size: A4; 
                margin: 0; 
              }
              @media print { 
                body { margin: 0; padding: 0; }
                .print-page-with-bg { box-shadow: none; margin: 0; }
              }
              
              .print-page-with-bg {
                width: 210mm;
                height: 297mm;
                margin: 0 auto;
                background-image: url('data:image/png;base64,${backgroundImage}');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                font-family: 'cairo', sans-serif;
                color: black;
                position: relative;
                padding: 0;
                page-break-after: always;
              }
              
              .print-page-with-bg:last-child {
                page-break-after: auto;
              }
              
              /* Date and Ref Section - Top Right with margin for header */
              .print-date-ref {
                position: absolute;
                top: 15%;
                right: 15mm;
                text-align: right;
                color: #004aad;
                font-size: 14px;
                line-height: 1.6;
              }

              .print-type-barcode {
                position: absolute;
                top: 15%;
                left: 15mm;
                text-align: center;
                color: #004aad;
                font-size: 14px;
              }

              .mail-type-label {
                font-weight: bold;
                margin-bottom: 6px;
              }

              .barcode-svg {
                display: block;
              }
              
              .ref-number, .mail-date {
                margin-bottom: 8px;
                font-weight: bold;
              }
              
              /* Mail Content Section */
              .print-mail-content.first-page {
                position: absolute;
                top: 25%; /* Below header and date/ref section */
                left: 20mm;
                right: 20mm;
                height: 45%; /* Fixed height to prevent overflow into footer */
                text-align: right;
                overflow: hidden;
              }
              
              .print-mail-content.continuation-page {
                position: absolute;
                top: 15%; /* Below header (10% + 5% buffer) */
                left: 20mm;
                right: 20mm;
                height: 65%; /* More space on continuation pages */
                text-align: right;
                overflow: hidden;
              }
              
              .mail-recipients {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 10px;
                text-align: right;
              }

              .mail-subject {
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 15px;
                text-align: right;
                text-decoration: underline;
              }
              
              .mail-body {
                font-size: 14px;
                line-height: 1.8;
                text-align: justify;
                text-indent: 20px;
                word-wrap: break-word;
                overflow-wrap: break-word;
              }
              
              /* Sender Section - Always at bottom with margin for footer */
              .print-sender-section {
                position: absolute;
                bottom: 10%;
                left: 20mm;
                right: 20mm;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                min-height: 120px;
              }

              .sender-left-group {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              }
              
              .sender-info-text {
                text-align: center;
                margin-bottom: 10px;
              }

              .sender-copy-note {
                font-size: 14px;
                align-self: flex-end;
              }

              .copy-recipient {
                font-size: 14px;
                line-height: 1.6;
              }

              .copy-recipient::before {
                content: '\\200B صورة إلى : ';
                visibility: hidden;
                font-weight: bold;
              }
              
              .sender-name {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              
              .sender-position {
                font-size: 14px;
                margin-bottom: 10px;
              }
              
              .stamps-section {
                display: flex;
                align-items: flex-end;
                gap: 20px;
              }
              
              .department-stamp {
                text-align: center;
                margin-left: 10px;
              }
              
              .department-stamp-img {
                width: 100px;
                height: 100px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
                border-radius: 50%;
              }
              
              .stamp-placeholder {
                width: 100px;
                height: 100px;
                border: 2px solid black;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
              }
              
              .stamp-text {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
              }
              
              .sender-signature {
                text-align: center;
              }
              
              .signature-image {
                width: 75px;
                height: 75px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
              }
              
              .signature-placeholder {
                width: 75px;
                height: 75px;
                border: 1px solid black;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
              }
              
              .signature-line {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
}

/* Recipients display styles */
.recipients-display {
  line-height: 1.4;
}

.recipient-group {
  display: inline-block;
  margin-left: 12px;
  font-size: 14px;
}

.recipient-group:first-child {
  margin-left: 0;
}

.recipients-detail {
  max-width: 70%;
}

.recipients-detail strong {
  color: #1976d2;
  font-weight: 600;
}
            </style>
          </head>
          <body>
            ${pagesHtml}
          </body>
          </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();

        const barcodeScript = printWindow.document.createElement('script');
        barcodeScript.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js';
        barcodeScript.onload = function() {
          var svgs = printWindow.document.querySelectorAll('.barcode-svg');
          for (var i = 0; i < svgs.length; i++) {
            var val = svgs[i].getAttribute('data-ref') || 'N/A';
            try {
              printWindow.JsBarcode(svgs[i], val, {
                format: 'CODE128',
                width: 1.5,
                height: 40,
                displayValue: false,
                margin: 5
              });
            } catch(e) { console.error('Barcode error:', e); }
          }
        };
        printWindow.document.head.appendChild(barcodeScript);
        
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 2500);
        
      } catch (error) {
        console.error('Error printing message:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في الطباعة: ' + (error.message || 'خطأ غير معروف')
        });
      }
    },

    formatPrintDate(date) {
      return new Date(date).toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      })
    },

    // Helper methods for print functionality
    async getImageAsBase64() {
      try {
        const response = await fetch(require('@/assets/printbg.png'));
        const blob = await response.blob();
        
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading background image:', error);
        return '';
      }
    },

    getRecipientTitle() {
      // Get the first to_department for the recipient title
      if (this.currentMessage && this.currentMessage.to && this.currentMessage.to.length > 0) {
        return `مدير ${this.currentMessage.to[0] || 'القسم'}`;
      }
      return 'مدير القسم';
    },

    getSenderName() {
      // Get sender name from current user or message data
      return this.$store.getters.user?.name || 'المرسل';
    },

    getSenderJobTitle() {
      // Get sender job title with department for sent mails
      // Since these are sent mails, use current user information
      const user = this.$store.getters.user;
      const currentDepartment = this.$store.getters.currentDepartment;
      const senderName = user?.name || 'المرسل';
      const departmentName = currentDepartment?.name || '';
      return departmentName ? `${senderName} - ${departmentName}` : senderName;
    }
  }
}
</script>

<style scoped>
.sent-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sent-toolbar {
  border-radius: 8px;
  background-color: #f8f9fa;
}

.sent-list {
  flex-grow: 1;
  overflow-y: auto;
  border-radius: 8px;
}

.search-field {
  width: 240px;
}

.selected-message {
  background-color: rgba(232, 240, 254, 0.8);
}

/* RTL specific styles */
.v-list-item__prepend {
  margin-left: 8px !important;
  margin-right: 0 !important;
}

.v-input--checkbox .v-input__slot {
  justify-content: flex-end;
}

.message-content {
  font-size: 14px;
  line-height: 1.6;
}

.attachment-item {
  border-radius: 4px;
  transition: background-color 0.2s;
}

.attachment-item:hover {
  background-color: #f5f5f5;
}

/* Date header styles */
.date-header {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.date-header:hover {
  background-color: #f0f0f0;
}

.date-header.expanded {
  background-color: #f0f0f0;
}

.transition-transform {
  transition: transform 0.3s ease-in-out;
}

.rotated {
  transform: rotate(90deg);
}

/* Print Styles */
@media print {
  * {
    --webkit-print-color-adjust: exact !important;
   --color-adjust: exact !important;
  }
  
  body {
    margin: 0;
    padding: 0;
  }
  
  .print-page-with-bg {
    box-shadow: none !important;
  }
}

/* Print Page with Background */
.print-page-with-bg {
  width: 210mm;
  height: 297mm;
  margin: 0 auto;
  background-image: url('@/assets/printbg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  font-family: 'cairo', sans-serif;
  color: black;
  page-break-after: always;
  position: relative;
  padding: 0;
}

/* Addressee Section - positioned after header space */
.print-addressee {
  position: absolute;
  top: 30%; /* Adjust based on your background image header height */
  right: 10%;
  text-align: right;
  margin-bottom: 20px;
}

.print-addressee p {
  font-size: 16px;
  font-weight: bold;
  margin: 3px 0;
  line-height: 1.4;
}

.print-addressee .respectful {
  margin-bottom: 15px;
}

/* Mail Content Section */
.print-mail-content {
  position: absolute;
  top: 160mm; /* Position below addressee */
  left: 20mm;
  right: 20mm;
  text-align: right;
}

.mail-subject {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: center;
  text-decoration: underline;
}

.mail-body {
  font-size: 14px;
  line-height: 1.8;
  text-align: justify;
  text-indent: 20px;
  margin-bottom: 20mm;
  max-height: 80mm; /* Limit height to prevent overflow */
  overflow: hidden;
}

/* Sender Section - positioned at bottom */
.print-sender-section {
  position: absolute;
  bottom: 40mm; /* Position above footer space */
  left: 20mm;
  right: 20mm;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.sender-left-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sender-info-text {
  text-align: center;
  margin-bottom: 10px;
}

.sender-copy-note {
  font-size: 14px;
  align-self: flex-end;
}

.sender-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
}

.sender-position {
  font-size: 14px;
  margin-bottom: 10px;
}

.stamps-section {
  display: flex;
  align-items: flex-end;
  gap: 20px;
}

/* Department Stamp */
.department-stamp {
  text-align: center;
  margin-left: 10px;
}

.department-stamp-img {
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 50%;
}

.stamp-placeholder {
  width: 100px;
  height: 100px;
  border: 2px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
}

.stamp-text {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
}

/* Sender Signature */
.sender-signature {
  text-align: center;
}

.signature-image {
  width: 75px;
  height: 75px;
  object-fit: contain;
}

.signature-placeholder {
  width: 75px;
  height: 75px;
  border: 1px solid black;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
}

.signature-line {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
}
</style> 